import Vue from 'vue'
import Vuex from 'vuex'
import api from '@/services/api'

Vue.use(Vuex)
export default new Vuex.Store({
  state: {
    stylist: '',
    loginError: '',
    clients: [],
    il8nContent: '',
    taskCount: 0,
    tutorials: [],
    isFetchingClients: false
  },
  mutations: {
    logoutUser (state) {
      localStorage.clear()
      state.stylist = ''
      state.clients = []
    },
    setClients (state, clientsData) {
      console.log('Setting clients in store:', clientsData.length, 'clients')
      // Initialize quotes array for all clients
      clientsData.forEach(client => {
        if (!client.quotes) {
          client.quotes = []
        }
      })
      state.clients = clientsData
      console.log('Clients set in store, total clients:', state.clients.length)
      console.log('Client IDs in store:', state.clients.map(c => c.id))
    },
    setStylist (state, user) {
      state.stylist = user
    },
    setLoginError (state, message) {
      state.loginError = message
    },
    setIl8nContent (state, content) {
      state.il8nContent = content
    },
    addClientToClients (state, client) {
      console.log('Adding client to store:', client)
      // Initialize quotes array for the new client
      client.quotes = []
      state.clients.push(client)
      console.log('Client added to store, total clients:', state.clients.length)
      console.log('Client IDs in store:', state.clients.map(c => c.id))
    },
    taskCount (state, count) {
      state.taskCount = count
    },
    setTutorials (state, tutorials) {
      state.tutorials = tutorials
    },
    updateThumbnailUrl (state, imageData) {
      if (imageData.type === 'stylist') {
        state.stylist.thumbnailUrl = imageData.imageUrl
      } else {
        state.clients.find(client => client.id === imageData.userId).thumbnailUrl = imageData.imageUrl
      }
    },
    updateStylist (state, stylistData) {
      state.stylist.paymentMethod = stylistData.paymentMethod
      state.stylist.paypalAddress = stylistData.paypalAddress
    },
    setClientQuotes (state, { clientId, quotes }) {
      const numericClientId = Number(clientId)
      const client = state.clients.find(c => c.id === numericClientId)
      if (client) {
        console.log('[STORE] Setting quotes for client:', clientId, 'count:', quotes.length)
        client.quotes = quotes
      } else {
        console.log('[STORE] Client not found in store:', numericClientId)
      }
    },
    updateQuoteSelection (state, { clientId, quoteId, included }) {
      const numericClientId = Number(clientId)
      const client = state.clients.find(c => c.id === numericClientId)
      if (client && client.quotes) {
        const quote = client.quotes.find(q => q.id === quoteId)
        if (quote) {
          quote.included = included
        }
      }
    },
    addClientQuote (state, { clientId, quote }) {
      const numericClientId = Number(clientId)
      const client = state.clients.find(c => c.id === numericClientId)
      if (client) {
        if (!client.quotes) {
          client.quotes = []
        }
        client.quotes.push(quote)
      }
    },
    removeClientQuote (state, { clientId, quoteId }) {
      const numericClientId = Number(clientId)
      const client = state.clients.find(c => c.id === numericClientId)
      if (client && client.quotes) {
        client.quotes = client.quotes.filter(q => q.id !== quoteId)
      }
    },
    clearClientQuotes (state, { clientId }) {
      const numericClientId = Number(clientId)
      const client = state.clients.find(c => c.id === numericClientId)
      if (client) {
        client.quotes = []
        console.log('Cleared quotes for client:', numericClientId)
      }
    }
  },
  actions: {
    getClients ({ state, commit }) {
      // Prevent duplicate calls if already loading
      if (state.isFetchingClients) {
        return Promise.resolve()
      }

      // Don't fetch if we already have clients
      if (state.clients && state.clients.length > 0) {
        console.log('[CLIENTS] Using cached clients from store, count:', state.clients.length)
        return Promise.resolve(state.clients)
      }

      console.log('[CLIENTS] Fetching clients from API')
      state.isFetchingClients = true

      return api.get('/api/stylist/clients', {
        headers: {
          'Content-Type': 'application/x.fashionphile.v1+json, application/json'
        }
      }).then(response => {
        const responseArray = response.data.data
        const clientsArray = []
        responseArray.forEach(function (clientObject) {
          const client = clientObject.attributes
          client.id = parseInt(clientObject.id)
          clientsArray.push(client)
        })
        commit('setClients', clientsArray)
        state.isFetchingClients = false
        return clientsArray
      }).catch(error => {
        state.isFetchingClients = false
        throw error
      })
    },
    addClientToClients ({ state }, client) {
      this.commit('addClientToClients', client)
    },
    taskCount ({ state }, count) {
      this.commit('taskCount', count)
    },
    setClients ({ dispatch }, clientsArray) {
      this.commit('setClients', clientsArray)
    },
    // Lightweight auth gate: the bare /api/account works for any authenticated
    // user and returns isStylist top-level. Only call ?include=stylist after
    // this passes — the include can 500 for non-stylists.
    verifyStylist ({ commit }) {
      return api.get('/api/account', {
        headers: {
          Accept: 'application/x.fashionphile.v1+json, application/json'
        }
      }).then(response => {
        const isStylist = !!(response && response.data.data.attributes.isStylist)
        commit('setLoginError', isStylist ? '' : 'not_a_stylist')
        return isStylist
      }).catch(() => {
        commit('setLoginError', 'We could not verify your account. Please try again.')
        return false
      })
    },
    getStylist ({ dispatch }) {
      return api.get('/api/account?include=stylist', {
        headers: {
          Accept: 'application/x.fashionphile.v1+json, application/json'
        }
      }).then(response => {
        if (!response) {
          return
        }
        const stylistData = response.data.data
        const includedData = Object.assign(stylistData, stylistData.attributes, response.data.included[0].attributes)
        this.commit('setStylist', includedData)
      })
    },
    getSnippet (context, key) {
      return api.post('/api/snippets', {
        category: 'stylist',
        key: key
      }, {
        headers: {
          'Content-Type': 'application/x.fashionphile.v1+json, application/json'
        }
      }).then(response => {
        return response.data.value
      }).catch((error) => {
        return error
      })
    },
    getIl8nContent ({ state }) {
      if (state.il8nContent) {
        return
      }
      return api.get('/api/i18n').then(response => {
        const language = 'en'
        const messages = response.data[language]
        const stylistMessages = {}
        Object.keys(messages).forEach(function (message) {
          if (message.includes('stylist.')) {
            stylistMessages[message.replace('stylist.', '')] = messages[message]
          }
        })
        this.commit('setIl8nContent', stylistMessages)
      })
    },
    uploadImage (context, formData) {
      return api.post('/api/stylist/upload-profile-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
    },
    updateImageUrl ({ state }, imageData) {
      this.commit('updateThumbnailUrl', imageData)
    },
    updateStylistData ({ state }, stylistData) {
      this.commit('updateStylist', stylistData)
    },
    // The web-app authenticates via the shared Clerk session now — no more
    // SSOToken cookie handoff, just navigate.
    authenticateAndRedirect (context, data) {
      window.location.href = data.url
    },
    async getClientQuotes ({ commit }, clientId) {
      try {
        const response = await api.get(`/api/stylist/clients/${clientId}/quotes`)
        const data = Array.isArray(response.data) ? response.data : response.data.data || []

        // Transform the data to include the included property
        const quotes = data.map(item => ({
          id: item.id,
          included: false,
          attributes: item
        }))

        commit('setClientQuotes', { clientId, quotes })
        return quotes
      } catch (error) {
        console.error('Error fetching client quotes:', error)
        throw error
      }
    },
    updateQuoteIncluded ({ commit }, { clientId, quoteId, included }) {
      commit('updateQuoteSelection', { clientId, quoteId, included })
    },
    addQuoteToClient ({ commit }, { clientId, quote }) {
      commit('addClientQuote', { clientId, quote })
    },
    removeQuoteFromClient ({ commit }, { clientId, quoteId }) {
      commit('removeClientQuote', { clientId, quoteId })
    },
    clearClientQuotes ({ commit }, clientId) {
      commit('clearClientQuotes', { clientId })
    }
  },
  getters: {
    getClientQuotes: (state) => (clientId) => {
      const numericClientId = Number(clientId)
      const client = state.clients.find(c => c.id === numericClientId)
      if (client) {
        return client.quotes || []
      }
      return []
    }
  }
})
